import {
  AsyncHandler,
  AsyncHandlerOf,
  HandlerOf,
} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {CancelToken} from '../../../../../libs/shared/helpers/src/utilities/asyncCancelHelpers';
import {SelectedColumnSort} from '../tableColumn';
import {TableSource} from './tableSource';

/*
 * Interfaces.
 */

export interface TableFetcherOptions {
  cancelToken: CancelToken;
}

export type TableFetcher = AsyncHandlerOf<TableFetcherOptions>;

export interface AsyncTableSourceOptions {
  fetcher: TableFetcher;

  /** Called when the sorting source is changed. */
  onSortChange?: HandlerOf<SelectedColumnSort>;
  /** Called when the data is cleared via filters, etc. */
  onReset?: AsyncHandler;
}

/*
 * Implementation.
 */

export class AsyncTableSource implements TableSource {
  private readonly fetcher: TableFetcher;
  private readonly onReset?: AsyncHandler;
  private readonly onSortChange?: HandlerOf<SelectedColumnSort>;

  constructor(options: AsyncTableSourceOptions) {
    this.fetcher = options.fetcher;
    this.onReset = options.onReset;
    this.onSortChange = options.onSortChange;
  }

  async loadAsync(cancelToken: CancelToken) {
    // Immediately fetch results
    await this.fetcher({
      cancelToken,
    });
  }

  async loadMoreAsync(cancelToken: CancelToken) {
    await this.loadAsync(cancelToken);
  }

  updateSort(sortOptions: SelectedColumnSort) {
    if (this.onSortChange) {
      this.onSortChange(sortOptions);
    }
  }

  async forceReloadAsync(cancelToken: CancelToken) {
    // Call reset so the table can clean up anything async
    if (this.onReset) {
      await this.onReset();
    }
    await this.loadAsync(cancelToken);
  }
}
